import { Injectable } from '@angular/core';

import { AuthorizationService, LocalStorageService, UiConfigurationJobSites, UiConfigurationService } from '@mirage-projects/cw-commons';
import {
    JobSiteDetailMenuOptions,
    ObjectBrowserFilter,
    ObjectBrowserFilterType,
    Project,
    ProjectType,
    SubMenuOption,
} from '@mirage-projects/cw-models';

import { MessageDialogOptions, MessageDialogOptionsButton, MessageDialogService } from '../message-dialog';

/** Service that contains the jobsite detail  business logic */
@Injectable({ providedIn: 'root' })
export class JobSiteDetailService {
    private filters: ObjectBrowserFilter[] = [];
    private jobSiteConfig: UiConfigurationJobSites | undefined = this.uiConfiguration.uiConfiguration.jobSites;

    constructor(
        private authorizationService: AuthorizationService,
        private uiConfiguration: UiConfigurationService,
        private localStorageService: LocalStorageService,
        private messageDialogService: MessageDialogService
    ) {}

    filterProjects(projects: Project[], projectsFilters: Map<string, string> | null): Project[] {
        return projectsFilters
            ? projects.filter((project) => {
                  for (const [key, value] of projectsFilters.entries()) {
                      if (
                          (key === 'search' && project.name.toLowerCase().indexOf(value.toLowerCase()) < 0) ||
                          (key === 'dataType' && project.projectType !== parseInt(value)) ||
                          (key === 'onlyMyProjects' && project.creatorUserId !== this.authorizationService.getUserLogged().userId)
                      ) {
                          return false;
                      }
                  }
                  return true;
              })
            : projects;
    }

    orderProjects(projects: Project[], projectsFilters: Map<string, string> | null): Project[] {
        const order = projectsFilters?.get('ordering') ?? '-accessedAt';

        switch (order) {
            case 'name':
                projects.sort((a, b) => (a.name > b.name ? 1 : -1));
                break;
            case '-name':
                projects.sort((a, b) => (a.name < b.name ? 1 : -1));
                break;
            case 'created_at':
                projects.sort((a, b) => {
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                });
                break;
            case '-created_at':
                projects.sort((a, b) => {
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                });
                break;
            case 'updated_at':
                projects.sort((a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime());
                break;
            case '-updated_at':
                projects.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
                break;
            case '-accessedAt':
                projects.sort((a, b) => new Date(b.accessedAt || 0).getTime() - new Date(a.accessedAt || 0).getTime());
                break;
            default:
                return projects;
        }

        return projects;
    }

    /**
     * function to get all the sub options for each type of projects
     */
    getSubMenuOptions(projectType: ProjectType): SubMenuOption[] {
        const defaultSubMenuOptions: SubMenuOption[] = [
            { keyText: 'PROJECTS.EDIT', id: JobSiteDetailMenuOptions.EDIT_PROJECT },
            { keyText: 'MODAL.DELETE_PROJECT', id: JobSiteDetailMenuOptions.DELETE_PROJECT },
            { keyText: 'TAB_PREFERENCES.PROJECT_PERMISSIONS', id: JobSiteDetailMenuOptions.EDIT_PROJECT_PERMISSIONS },
        ];

        if (projectType === ProjectType.SCAN) {
            return [
                ...defaultSubMenuOptions,
                { keyText: 'PROJECTS.OPEN_DEFAULT_VIEWER', id: JobSiteDetailMenuOptions.OPEN_DEFAULT_VIEWER },
            ];
        }

        return defaultSubMenuOptions;
    }

    initFilters(): ObjectBrowserFilter[] {
        this.filters = [];

        if (!this.jobSiteConfig?.filters || this.jobSiteConfig.filters.includes('name')) {
            this.filters.push({
                type: ObjectBrowserFilterType.SEARCH,
                fieldName: 'name',
                queryField: 'search',
                placeholder: 'JOB_SITE.DETAIL.FILTER',
                disable: false,
            });
        }

        if (!this.jobSiteConfig?.filters || this.jobSiteConfig.filters.includes('dataType')) {
            this.filters.push({
                type: ObjectBrowserFilterType.SELECT,
                fieldName: 'dataType',
                queryField: 'dataType',
                text: 'PROJECTS.SORT_BY',
                range: [
                    {
                        value: '',
                        text: 'PROJECTS.ALL_TYPES',
                    },
                    {
                        value: 3,
                        text: 'PROJECTS.SCAN_TYPE',
                    },
                ],
                selected: 3,
                value: 3,
                disable: true,
            });
        }

        if (!this.jobSiteConfig?.filters || this.jobSiteConfig.filters.includes('ordering')) {
            const value = this.localStorageService.getItem('jobSiteDataOrder') ?? '-created_at';

            const projectsOrderFilter = {
                type: ObjectBrowserFilterType.SELECT,
                fieldName: 'ordering',
                queryField: 'ordering',
                text: 'OBJECT_BROWSER.ORDER_BY',
                selected: value,
                value,
                range: [
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_MOST_RECENTLY_OPENED',
                        value: '',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_NAME_ASC',
                        value: 'name',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_NAME_DESC',
                        value: '-name',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_ASC',
                        value: 'created_at',
                    },
                    {
                        text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_DESC',
                        value: '-created_at',
                    },
                ],
                onChange: (data?: ObjectBrowserFilter): void => {
                    this.localStorageService.setItem('jobSiteDataOrder', data?.value, true);

                    const orderingFilter = this.filters.find((filter) => filter.fieldName === 'ordering');
                    if (orderingFilter) {
                        orderingFilter.selected = data?.value;
                    }
                },
                width: '15rem',
                disable: false,
            };
            this.filters.push(projectsOrderFilter);
        }

        if (!this.jobSiteConfig?.filters || this.jobSiteConfig.filters.includes('onlyUser')) {
            this.filters.push({
                type: ObjectBrowserFilterType.TOGGLE,
                fieldName: 'onlyUser',
                queryField: 'onlyMyProjects',
                text: 'JOB_SITE.DETAIL.SHOW_MY_PROJECTS_LAYERS',
                tooltip: 'JOB_SITE.DETAIL.SHOW_MY_PROJECTS_LAYERS_TOOLTIP',
                trueValue: true,
                value: false,
                defaultValue: false,
                disableList: [
                    {
                        field: 'author',
                        when: true,
                    },
                ],
                disable: false,
            });
        }
        return this.filters;
    }

    acceptDeleteButton(): MessageDialogOptionsButton {
        return {
            id: 'delete',
            text: 'GENERAL.DELETE',
            primary: true,
        };
    }

    async showDeleteProjectPopup(): Promise<MessageDialogOptionsButton> {
        const acceptButton = this.acceptDeleteButton();
        const cancelButton = this.messageDialogService.getCancelButtonConfig();
        const options: Partial<MessageDialogOptions> = {
            style: 'primary',
            headerText: 'MODAL.DELETE_PROJECT',
            contentText: 'JOB_SITE.DETAIL.DELETE_PROJECTS_CONFIRM_MSG',
            contentSecondaryText: 'JOB_SITE.DETAIL.NOTE',
            buttonsList: [cancelButton, acceptButton],
        };

        const resultBtnCfg = await this.messageDialogService.showInformation(options);

        return resultBtnCfg;
    }
}
